import { Divider, Modal } from "antd";
import { FC } from "react";

interface ProductItem {
  id: string | number;
  productName: string;
  qty: number;
  price: string | number;
  isRedemptionItem?: boolean;
  redemptionItem?: any; // RedemptionItem type
}

interface ReceiptModalProps {
  isOpen: boolean;
  onClose: () => void;
  productItems: ProductItem[];
  subtotal: number;
  discount?: number;
  promotionCode?: string;
  deliveryCost?: number;
  total: number;
  points?: number;
  pointsUsed?: number;
  shippingAddress?: string;
  shippingMethod?: string;
  paymentMethod?: string;
}

const ReceiptModal: FC<ReceiptModalProps> = ({
  isOpen,
  onClose,
  productItems,
  subtotal,
  discount = 0,
  promotionCode,
  deliveryCost = 0,
  total,
  points = 0,
  pointsUsed = 0,
  shippingAddress,
  shippingMethod,
  paymentMethod,
}) => {
  return (
    <Modal
      title=""
      open={isOpen}
      onCancel={onClose}
      footer={null}
      styles={{
        content: {
          padding: 0,
        },
        body: {
          padding: 0,
        },
      }}
      width={450}
    >
      <div className="receipt-container p-4 bg-gray-50 rounded-md">
        <div className="items-list mb-4">
          <h3 className="font-bold mb-3">สรุปรายการสั่งซื้อ</h3>

          {/* Products Section */}
          {productItems.filter((item) => !item.isRedemptionItem).length > 0 && (
            <div className="mb-4">
              <div className="text-sm font-semibold text-gray-800 mb-2 flex items-center">
                <span className="mr-1">🛍️</span>
                สินค้า (
                {productItems.filter((item) => !item.isRedemptionItem).length})
              </div>

              {/* Table Header */}
              <div className="grid grid-cols-12 text-sm font-medium text-gray-700 border-b pb-2 mb-2">
                <div className="col-span-6">สินค้า</div>
                <div className="col-span-2 text-center">จำนวน</div>
                <div className="col-span-2 text-center">ราคา</div>
                <div className="col-span-2 text-center">รวม</div>
              </div>

              {/* Table Rows */}
              {productItems
                .filter((item) => !item.isRedemptionItem)
                .map((item) => (
                  <div
                    key={item.id}
                    className="grid grid-cols-12 py-2 text-sm border-b border-gray-100 items-center"
                  >
                    <div className="col-span-6 pr-2">
                      <p className="truncate">{item.productName}</p>
                    </div>
                    <div className="col-span-2 text-center">{item.qty}</div>
                    <div className="col-span-2 text-center">
                      ฿{parseFloat(item.price.toString()).toLocaleString()}
                    </div>
                    <div className="col-span-2 text-center font-medium">
                      ฿
                      {(
                        parseFloat(item.price.toString()) * item.qty
                      ).toLocaleString()}
                    </div>
                  </div>
                ))}
            </div>
          )}

          {/* Redemption Items Section */}
          {productItems.filter((item) => item.isRedemptionItem).length > 0 && (
            <div className="mb-4">
              <div className="text-sm font-semibold text-purple-600 mb-2 flex items-center">
                <span className="mr-1">🎁</span>
                สินค้าแลกคะแนน (
                {productItems.filter((item) => item.isRedemptionItem).length})
              </div>
              {/* Table Header */}
              <div className="grid grid-cols-12 text-sm font-medium text-gray-700 border-b pb-2 mb-2">
                <div className="col-span-6">สินค้า</div>
                <div className="col-span-2 text-center">จำนวน</div>
                <div className="col-span-2 text-center">ราคา</div>
                <div className="col-span-2 text-center">รวม</div>
              </div>

              {/* Table Rows */}
              {productItems
                .filter((item) => item.isRedemptionItem)
                .map((item) => (
                  <div
                    key={item.id}
                    className="grid grid-cols-12 py-2 text-sm border-b border-gray-100 items-center"
                  >
                    <div className="col-span-6 pr-2">
                      <p className="truncate">{item.productName}</p>
                    </div>
                    <div className="col-span-2 text-center">{item.qty}</div>
                    <div className="col-span-2 text-center">
                      {item.isRedemptionItem ? (
                        <div>
                          <div className="text-primary-500 text-xs">
                            {item.redemptionItem?.pointsCost} pts
                          </div>
                          {item.redemptionItem?.type === "HYBRID" && (
                            <div className="text-xs">
                              + ฿
                              {parseFloat(
                                item.price.toString()
                              ).toLocaleString()}
                            </div>
                          )}
                        </div>
                      ) : (
                        `฿${parseFloat(item.price.toString()).toLocaleString()}`
                      )}
                    </div>
                    <div className="col-span-2 text-center font-medium">
                      {item.isRedemptionItem ? (
                        <div>
                          <div className="text-primary-500 text-xs">
                            {item.redemptionItem?.pointsCost} pts
                          </div>
                          {item.redemptionItem?.type === "HYBRID" && (
                            <div className="text-xs">
                              + ฿
                              {(
                                parseFloat(item.price.toString()) * item.qty
                              ).toLocaleString()}
                            </div>
                          )}
                        </div>
                      ) : (
                        `฿${(
                          parseFloat(item.price.toString()) * item.qty
                        ).toLocaleString()}`
                      )}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>

        <div className="summary">
          <div className="flex justify-between mb-2">
            <p>ยอดรวม</p>
            <p>฿{subtotal.toLocaleString()}</p>
          </div>
          <Divider className="my-2" />

          {discount > 0 && (
            <div className="flex justify-between mb-2">
              <p>
                {promotionCode ? (
                  <>
                    โค้ดส่วนลด{" "}
                    <span className="text-primary font-medium">
                      {promotionCode}
                    </span>
                  </>
                ) : (
                  "ส่วนลด"
                )}
              </p>
              <p className="text-danger-500">-฿{discount.toLocaleString()}</p>
            </div>
          )}
          <div className="flex justify-between mb-2">
            <p>ค่าจัดส่ง</p>
            <p>
              {deliveryCost > 0
                ? `฿${deliveryCost.toLocaleString()}`
                : "รอการประเมินราคา"}
            </p>
          </div>
          <div className="flex justify-between font-bold bg-[#E8F8CCE8] p-2 mt-2">
            <p>ยอดรวมทั้งสิ้น</p>
            <p>฿{total.toLocaleString()}</p>
          </div>
          {pointsUsed > 0 && (
            <div className="flex justify-between mt-2 text-purple-600 font-semibold">
              <p>คะแนนที่ใช้สิ่งแลกของ</p>
              <p>{pointsUsed.toLocaleString()} คะแนน</p>
            </div>
          )}
          {points > 0 && (
            <div className="flex justify-between mt-2 text-primary-700">
              <p>คะแนนที่ได้ทั้งหมด</p>
              <p>{points.toLocaleString()} คะแนน</p>
            </div>
          )}

          {(shippingAddress || shippingMethod || paymentMethod) && (
            <>
              <Divider className="my-2" />

              <div className="shipping-info text-sm">
                <h3 className="font-bold mb-2">ข้อมูลการจัดส่ง</h3>
                {shippingAddress && <div>{shippingAddress}</div>}
                {shippingMethod && (
                  <p className="mt-2">วิธีการจัดส่ง: {shippingMethod}</p>
                )}
                {paymentMethod && <p>วิธีการชำระเงิน: {paymentMethod}</p>}
              </div>

              <div className="text-center mt-4">
                <p className="text-gray-500 text-sm">ขอบคุณที่ใช้บริการ</p>
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ReceiptModal;
